import React from 'react'
import PropTypes from 'prop-types'
import qs from 'qs'

import { t } from '@ulule/localize'

// TODO: This should eventually be put in common
import { fetchSearchProjects, fetchSearchMoreProjects } from '$features/discover/services'

const DEFAULT_SORTING = 'popular'
const DEFAULT_OFFSET = 0

export const DEFAULT_LIMIT = 60

const SEARCH_API_FILTERS = {
  categories: {
    defaultValue: 'all',
    qualifier: 'tag_id',
    valueAttribute: 'id'
  },
  channels: {
    defaultValue: 'all',
    qualifier: 'channel_ids',
    valueAttribute: 'id'
  },
  cities: {
    defaultValue: 'all',
    qualifier: 'city_id',
    valueAttribute: 'id'
  },
  countries: {
    defaultValue: 'all',
    qualifier: 'country',
    valueAttribute: 'value'
  },
  languages: {
    defaultValue: 'all',
    qualifier: 'lang',
    valueAttribute: 'value'
  },
  statuses: {
    defaultValue: 'all',
    qualifier: 'status',
    valueAttribute: 'value'
  },
  regions: {
    defaultValue: 'all',
    qualifier: 'region_id',
    valueAttribute: 'id'
  },
  quality_score__not: {
    defaultValue: '',
    qualifier: 'quality_score__not',
    valueAttribute: 'value'
  }
}

const getDisplayName = WrappedComponent => WrappedComponent.displayName || WrappedComponent.name || 'Component'

export const withProjectResults = WrappedComponent => {
  class ProjectSearchResults extends React.Component {
    constructor(props) {
      super(props)

      const { excludeXRatedProjects, initialSettings, limit } = props

      const { sorting, ...filtersFromQueryString } = window.location && this.parseQueryString(window.location.search)
      const sortingFromQueryString = sorting && sorting.length > 0 && sorting[sorting.length - 1]

      this.state = {
        currentFilters: {
          ...(excludeXRatedProjects && { quality_score__not: ['X'] }),
          ...initialSettings.filters,
          ...filtersFromQueryString
        },
        currentSorting: sortingFromQueryString || initialSettings.sorting || DEFAULT_SORTING,
        error: null,
        errorFetchingMore: false,
        isFetching: true,
        isFetchingMore: false,
        meta: {
          totalCount: 0,
          next: null,
          offset: DEFAULT_OFFSET,
          limit
        },
        results: []
      }
    }

    componentDidMount() {
      this.requestResults()
    }

    componentDidUpdate(prevProps, prevState) {
      // Fetch projects each time filters or sorting is updating
      // TODO: add deeper comparison to be more performant
      const { currentFilters, currentSorting } = this.state

      if (prevState.currentFilters !== currentFilters || prevState.currentSorting !== currentSorting) {
        this.requestResults()
      }
    }

    parseQualifiers = () => {
      const { currentFilters, currentSorting } = this.state

      const input = {
        sort: currentSorting
      }

      Object.keys(currentFilters).forEach(key => {
        const defaultValue = SEARCH_API_FILTERS && SEARCH_API_FILTERS[key] && SEARCH_API_FILTERS[key].defaultValue

        if (
          defaultValue &&
          currentFilters[key] &&
          currentFilters[key].length > 0 &&
          currentFilters[key][0] !== defaultValue
        ) {
          const qualifier = SEARCH_API_FILTERS && SEARCH_API_FILTERS[key] && SEARCH_API_FILTERS[key].qualifier
          input[qualifier] = currentFilters[key]
        }
      })

      return input
    }

    handleCloseError = event => {
      event.preventDefault()

      this.setState({ error: null })
    }

    handleFetchingError = (error, isFetchingMore = false) => {
      const errorProps = {
        level: 'alert',
        message: t(
          'Oops... an error occured. Please refresh the page to retry. If the error persists, please contact support.'
        ),
        ...error
      }

      this.setState({
        ...this.oldState,
        isFetching: false,
        isFetchingMore: false,
        error: errorProps,
        errorFetchingMore: isFetchingMore
      })
    }

    handleFetchingPending = (isFetchingMore = false) => {
      const { results } = this.state

      // Save the old state in case of error
      this.oldState = this.state

      this.setState({
        results: isFetchingMore ? results : [],
        isFetching: !isFetchingMore,
        isFetchingMore
      })
    }

    handleFetchingSuccess = (results, meta) => {
      this.setState({
        isFetching: false,
        isFetchingMore: false,
        results: results || [],
        meta: {
          ...meta,
          totalCount: meta.total_count
        }
      })
    }

    handleLoadMore = event => {
      event.preventDefault()

      this.requestMoreResults()
    }

    parseQueryString = queryParams => {
      const parameters = qs.parse(queryParams, { ignoreQueryPrefix: true })
      const normalizedParamaters = {}

      Object.keys(parameters).forEach(key => {
        if (Array.isArray(parameters[key])) {
          normalizedParamaters[key] = parameters[key]
        } else {
          normalizedParamaters[key] = [parameters[key]]
        }
      })

      return normalizedParamaters
    }

    setQueryParam = (name, value, reset = false) => {
      const currentQueryParams = reset ? '' : window.location.search

      // Parse current query params in location
      // and add new query param
      const parsed = qs.parse(currentQueryParams, { ignoreQueryPrefix: true })
      parsed[name] = value

      // Stringify all new query params
      const stringified = qs.stringify(parsed)

      // Replace current URL in history
      const character = stringified.length > 0 ? '?' : ''
      window.history.replaceState(null, null, `${window.location.pathname}${character}${stringified}`)
    }

    requestMoreResults = async () => {
      this.handleFetchingPending(true)

      const { meta, results } = this.state

      try {
        const { userIsAuthenticated } = this.props
        const response = await fetchSearchMoreProjects(meta.next, userIsAuthenticated)
        const allResults = [...results, ...response.projects]

        this.handleFetchingSuccess(allResults, response.meta)
      } catch (error) {
        console.log(error)
        this.handleFetchingError(error, true)
      }
    }

    requestResults = async () => {
      this.handleFetchingPending(false)

      const { meta } = this.state

      try {
        const { currentLanguage, userIsAuthenticated } = this.props
        const qualifiers = this.parseQualifiers()
        const params = { lang: currentLanguage, limit: meta.limit }

        const response = await fetchSearchProjects(null, qualifiers, params, userIsAuthenticated)

        this.handleFetchingSuccess(response.projects, response.meta)
      } catch (error) {
        this.handleFetchingError(error)
      }
    }

    updateFilter = (filterName, optionValue) => {
      const { limit } = this.props
      const { currentFilters } = this.state

      this.setState({
        currentFilters: {
          ...currentFilters,
          [filterName]: [optionValue]
        },
        meta: {
          offset: DEFAULT_OFFSET,
          limit,
          totalCount: 0,
          next: null,
          previous: null
        }
      })

      this.setQueryParam(filterName, optionValue)
    }

    updateSorting = ({ value }) => {
      this.setState({ currentSorting: value })
      this.setQueryParam('sorting', value)
    }

    render() {
      const {
        currentFilters,
        currentSorting,
        errorFetchingMore,
        isFetching,
        isFetchingMore,
        meta,
        results
      } = this.state
      const { initialSettings } = this.props

      const currentStatus = currentFilters.statuses.length > 0 && currentFilters.statuses[0]
      const initialStatus = initialSettings.filters && initialSettings.filters.statuses[0]
      const projectCount = meta && meta.total_count

      const noProjectWithInitialParams = !isFetching && !projectCount && currentStatus === initialStatus

      return (
        <WrappedComponent
          {...this.props}
          currentFilters={currentFilters}
          currentSorting={currentSorting}
          errorFetchingMore={errorFetchingMore}
          isFetching={isFetching}
          isFetchingMore={isFetchingMore}
          loadMoreProjects={this.handleLoadMore}
          noProjectWithInitialParams={noProjectWithInitialParams}
          moreProjectsToFetch={meta && meta.next}
          projectCount={projectCount}
          results={results}
          updateFilter={this.updateFilter}
          updateSorting={this.updateSorting}
        />
      )
    }
  }

  ProjectSearchResults.defaultProps = {
    excludeXRatedProjects: false,
    initialSettings: {},
    limit: DEFAULT_LIMIT,
    userIsAuthenticated: false
  }

  ProjectSearchResults.displayName = `WithSearchResults(${getDisplayName(WrappedComponent)})`

  ProjectSearchResults.propTypes = {
    currentLanguage: PropTypes.string.isRequired,
    excludeXRatedProjects: PropTypes.bool,
    initialSettings: PropTypes.shape({
      filters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])))
        .isRequired,
      sorting: PropTypes.string
    }),
    userIsAuthenticated: PropTypes.bool,
    limit: PropTypes.number
  }
  return ProjectSearchResults
}
